import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface FilterComboboxOption {
  readonly value: string;
  readonly label: string;
  /**
   * Secondary line rendered under the label. This is what makes two identically
   * named records distinguishable, so callers should pass the qualifying
   * context (owning workspace, deployment) rather than pre-mangling the label.
   */
  readonly description?: string;
}

export interface FilterComboboxProps {
  readonly value: string;
  readonly options: readonly FilterComboboxOption[];
  readonly onChange: (value: string) => void;
  readonly id?: string;
  /** Only for callers with no visible label; a visible label names the control better. */
  readonly ariaLabel?: string;
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: string;
  readonly className?: string;
}

/**
 * Single-select filter control that stays usable when the option set is large:
 * the list is searchable, and options carry a secondary line so duplicate names
 * are told apart by context instead of by a mangled label.
 */
export function FilterCombobox({
  value,
  options,
  onChange,
  id,
  ariaLabel,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches.',
  className,
}: FilterComboboxProps): ReactNode {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
          id={id}
          role="combobox"
          type="button"
          variant="outline"
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
        >
          <span className="truncate">{selected?.label ?? placeholder}</span>
          <ChevronsUpDownIcon data-icon="inline-end" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[max(var(--radix-popover-trigger-width),18rem)] p-0"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  value={`${option.label} ${option.description ?? ''} ${option.value}`}
                >
                  <CheckIcon
                    aria-hidden="true"
                    className={cn(option.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{option.label}</span>
                    {option.description === undefined ? null : (
                      <span className="truncate text-xs text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
